import React, { useState, useCallback, useRef } from 'react';
import { X, Plus } from 'lucide-react';

/**
 * Input à "chips" (tags) pour listes courtes (emails, téléphones, tags…).
 * - Enter, virgule, ou point-virgule → ajoute le token courant
 * - Backspace sur input vide → retire le dernier chip
 * - Croix sur chip → retire ce chip
 * - Coller ("paste") plusieurs valeurs séparées par virgule/espace/point-virgule → tout ajouter d'un coup
 *
 * Props:
 *  - value: string[] — liste actuelle
 *  - onChange: (next: string[]) => void
 *  - placeholder: string
 *  - validate?: (token: string) => string | true — retourne message d'erreur ou true pour OK
 *  - normalize?: (token: string) => string — normalisation (trim, lower, etc.)
 *  - testId: préfixe pour data-testid
 */
export const MultiTokenInput = ({
  value = [],
  onChange,
  placeholder = 'Ajouter…',
  validate,
  normalize = (s) => s.trim(),
  testId = 'multi-token',
  className = '',
  chipClassName = 'bg-slate-100 text-slate-700 border-slate-200',
}) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const addTokens = useCallback((raw) => {
    // Split sur virgule, point-virgule, saut de ligne ou espace multiple.
    const parts = String(raw || '').split(/[,;\n]+/).map(normalize).filter(Boolean);
    if (parts.length === 0) return;
    const uniq = [...value];
    let firstErr = null;
    for (const p of parts) {
      if (uniq.includes(p)) continue;
      if (validate) {
        const res = validate(p);
        if (res !== true) { if (!firstErr) firstErr = res || 'Valeur invalide'; continue; }
      }
      uniq.push(p);
    }
    onChange(uniq);
    setError(firstErr);
  }, [value, onChange, normalize, validate]);

  const commitDraft = () => {
    if (!draft.trim()) return;
    addTokens(draft);
    setDraft('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      // Retire le dernier chip
      onChange(value.slice(0, -1));
      setError(null);
    }
  };

  const onPaste = (e) => {
    const pasted = e.clipboardData?.getData('text') || '';
    if (/[,;\n]/.test(pasted)) {
      e.preventDefault();
      addTokens(pasted);
    }
  };

  const remove = (i) => {
    const next = value.filter((_, idx) => idx !== i);
    onChange(next);
    setError(null);
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1.5 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-200"
        onClick={() => inputRef.current?.focus()}
        data-testid={`${testId}-container`}
      >
        {value.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${chipClassName}`}
            data-testid={`${testId}-chip-${i}`}
          >
            <span className="max-w-[220px] truncate">{v}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); remove(i); }}
              className="text-slate-400 hover:text-red-500"
              aria-label={`Retirer ${v}`}
              data-testid={`${testId}-remove-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (error) setError(null); }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={commitDraft}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] border-0 bg-transparent p-0 text-sm outline-none placeholder:text-slate-400"
          data-testid={`${testId}-input`}
        />
        {draft.trim() && (
          <button
            type="button"
            onClick={commitDraft}
            className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 hover:bg-blue-100"
            data-testid={`${testId}-add`}
          >
            <Plus className="h-3 w-3" /> Ajouter
          </button>
        )}
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] text-slate-500">
        <span>Appuyez sur <b>Entrée</b> ou <b>virgule</b> pour ajouter. <b>Backspace</b> pour supprimer le dernier.</span>
        {value.length > 0 && (
          <span data-testid={`${testId}-count`}>{value.length} entrée{value.length > 1 ? 's' : ''}</span>
        )}
      </div>
      {error && <div className="mt-1 text-[11px] text-red-600" data-testid={`${testId}-error`}>{error}</div>}
    </div>
  );
};

// Validateurs prêts à l'emploi ------------------------------------------------
export const validateEmail = (s) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(s) ? true : `Email invalide : ${s}`;
};

export const validatePhone = (s) => {
  // E.164 souple : + optionnel, 6 à 15 chiffres (avec espaces / tirets tolérés).
  const cleaned = s.replace(/[\s.-]/g, '');
  const re = /^\+?[0-9]{6,15}$/;
  return re.test(cleaned) ? true : `Numéro invalide : ${s}`;
};

export const normalizeEmail = (s) => s.trim().toLowerCase();
export const normalizePhone = (s) => s.replace(/\s+/g, '').trim();

export default MultiTokenInput;
